import React, { useEffect, useMemo, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { appDocumentTitle } from '../../appMeta';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import {
  strapiAddQuotationSampleFromSampling,
  strapiListSamplingPlansForWorkOrder,
  strapiListSurveyRows,
  strapiPreviewQuotationSampleSync,
  strapiSyncQuotationSamplesForWorkOrder,
  type QuotationSampleSyncPreview,
} from '../../api/strapiCrm';
import { QuotationSampleFormDialog } from '../QuotationsPage/QuotationFormRoute/TabScreens/SampleFormDialog';
import type { QuotationFormSampleRow } from '../QuotationsPage/QuotationFormRoute/quotationFormTypes';
import { queryKeys } from '../../query/queryKeys';
import { useWorkOrderDetailQuery } from '../../query/hooks';
import { PAGE_SHELL_TABS_SX, PageShell } from '../../layout/PageShell';
import { CollapsibleKeteranganSplit } from '../../ui/CollapsibleKeteranganSplit';
import { SurveyBeritaAcaraGrid } from './components/SurveyBeritaAcaraGrid';
import {
  applyCanonicalPlansToSurveyRows,
  computeWorkOrderPlanChangeSummary,
  workOrderPlanChangeBannerMessage,
} from './surveyPlanChangeUtils';
import { SurveyWorkOrderPlansReadonly } from './components/SurveyWorkOrderPlansReadonly';
import { SurveyStartSamplingPlanModal } from './components/SurveyStartSamplingPlanModal';
import { SurveyQuotationSampleSyncDialog } from './components/SurveyQuotationSampleSyncDialog';
import { tambahTitikOptionsFromPlans } from './components/surveyTambahTitik';
import { samplesHaveAirGroup } from '../SamplingWorkOrdersPage/samplingPlanAirGroup';
import { isAirGroupChecklistPlan } from '../SamplingWorkOrdersPage/workOrderSamplingPlanEnsure';
import { workOrderStatusDisplay } from '../SamplingWorkOrdersPage/workOrderShared';
import { allVisibleSamplesHaveEquipmentChecklist } from '../SamplingWorkOrdersPage/workOrderEquipmentChecklist';
import { filterSamplesForWorkOrderSamplingPlan } from '../SamplingSchedulesPage/samplingSchedulePlans';
import { quotationUsesSampleGroups } from '../QuotationsPage/quotationWorkflow';
import { SurveyCustomerAddressForm } from './components/SurveyCustomerAddressForm';
import { buildSurveyDetailHash, parseSurveyDetailHash, type SurveyDetailTab } from './surveyDetailRouteHash';
import { quotationCustomerEmail, resolveWorkOrderDisplayCustomer } from './workOrderDisplayCustomer';

export function SurveyDetailRoute() {
  const { jwt } = useAuth();
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { workOrderDocumentId: rawWorkOrderDocumentId } = useParams<{ workOrderDocumentId: string }>();
  const workOrderDocumentId = rawWorkOrderDocumentId ? decodeURIComponent(rawWorkOrderDocumentId) : null;
  const tab = useMemo(() => parseSurveyDetailHash(location.hash), [location.hash]);
  const [planChecklistModalOpen, setPlanChecklistModalOpen] = useState(false);
  const [syncPreview, setSyncPreview] = useState<QuotationSampleSyncPreview | null>(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncDialogError, setSyncDialogError] = useState<string | null>(null);
  const [editQuotationOpen, setEditQuotationOpen] = useState(false);
  const [editQuotationError, setEditQuotationError] = useState<string | null>(null);
  const [editQuotationSuccess, setEditQuotationSuccess] = useState<string | null>(null);

  useDocumentTitle(appDocumentTitle('Detail sampling'));
  const detailQ = useWorkOrderDetailQuery(jwt, workOrderDocumentId);
  const plansQ = useQuery({
    queryKey: ['strapi', 'samplingPlansByWorkOrder', jwt, workOrderDocumentId],
    queryFn: () => strapiListSamplingPlansForWorkOrder(jwt!, workOrderDocumentId!),
    enabled: Boolean(jwt && workOrderDocumentId),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const rowsQ = useQuery({
    queryKey: queryKeys.surveyRowsForWorkOrder(jwt, workOrderDocumentId),
    queryFn: () =>
      strapiListSurveyRows(jwt!, {
        userId: null,
        workOrderDocumentId,
        syncSamplingPlans: true,
      }),
    enabled: Boolean(jwt && workOrderDocumentId),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const quotationSampleSyncPreviewQ = useQuery({
    queryKey: ['strapi', 'quotationSampleSyncPreview', jwt, workOrderDocumentId],
    queryFn: () => strapiPreviewQuotationSampleSync(jwt!, workOrderDocumentId!),
    enabled: Boolean(jwt && workOrderDocumentId),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const displayRows = useMemo(
    () =>
      applyCanonicalPlansToSurveyRows(
        rowsQ.data ?? [],
        plansQ.data ?? [],
        detailQ.data?.sample_informations ?? [],
      ),
    [rowsQ.data, plansQ.data, detailQ.data?.sample_informations],
  );
  const planChangeSummary = useMemo(
    () =>
      computeWorkOrderPlanChangeSummary(displayRows, plansQ.data ?? [], detailQ.data?.quotation),
    [displayRows, plansQ.data, detailQ.data?.quotation],
  );
  const titikPlanOptions = useMemo(
    () =>
      tambahTitikOptionsFromPlans(
        plansQ.data ?? [],
        detailQ.data?.sample_informations ?? [],
        displayRows,
      ),
    [plansQ.data, detailQ.data?.sample_informations, displayRows],
  );

  useEffect(() => {
    if (!quotationSampleSyncPreviewQ.data?.has_pending) {
      setSyncPreview(null);
      setSyncDialogOpen(false);
      return;
    }
    setSyncPreview(quotationSampleSyncPreviewQ.data);
    setSyncDialogOpen(true);
  }, [quotationSampleSyncPreviewQ.data]);

  useEffect(() => {
    setSyncDialogError(null);
  }, [workOrderDocumentId]);

  useEffect(() => {
    const expected = buildSurveyDetailHash(parseSurveyDetailHash(location.hash));
    if (location.hash === expected) return;
    navigate(
      { pathname: location.pathname, search: location.search, hash: expected },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  function goTab(next: SurveyDetailTab) {
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: buildSurveyDetailHash(next),
      },
      { replace: true },
    );
  }

  const addQuotationSampleMut = useMutation({
    mutationFn: async (row: QuotationFormSampleRow) => {
      if (!jwt || !workOrderDocumentId) throw new Error('Sesi tidak valid.');
      const jt = Number.parseInt(row.jumlahTitik.trim(), 10);
      if (!Number.isFinite(jt) || jt < 1) throw new Error('Jumlah titik minimal 1.');
      const hTrim = row.harga.trim();
      const harga = hTrim === '' ? null : Number.parseFloat(hTrim.replace(',', '.'));
      if (hTrim !== '' && (!Number.isFinite(harga) || (harga ?? 0) < 0)) {
        throw new Error('Harga harus angka ≥ 0 atau dikosongkan.');
      }
      return strapiAddQuotationSampleFromSampling(jwt, {
        workOrderDocumentId,
        jenisPengukuranDocumentId: row.jenisPengukuranMasterId,
        bakuMutuDocumentId: row.bakuMutuMasterId,
        parameterUji: row.parameterUji,
        samplePointCount: jt,
        harga,
      });
    },
    onSuccess: async (result) => {
      setEditQuotationError(null);
      setEditQuotationOpen(false);
      setEditQuotationSuccess(
        `Sampel baru ditambahkan. Quotation masuk Need Attention (${result.sample_point_count} titik).`,
      );
      await qc.invalidateQueries({
        queryKey: ['strapi', 'quotationSampleSyncPreview', jwt, workOrderDocumentId],
      });
      await qc.invalidateQueries({ queryKey: queryKeys.surveyRowsPrefix(jwt) });
      if (workOrderDocumentId) {
        await qc.invalidateQueries({
          queryKey: queryKeys.surveyRowsForWorkOrder(jwt, workOrderDocumentId),
        });
        await qc.invalidateQueries({ queryKey: queryKeys.workOrderDetail(jwt, workOrderDocumentId) });
      }
      await qc.invalidateQueries({
        queryKey: ['strapi', 'samplingPlansByWorkOrder', jwt, workOrderDocumentId],
      });
    },
    onError: (error) => {
      setEditQuotationError(error instanceof Error ? error.message : 'Gagal menambah sampel quotation.');
    },
  });

  const syncQuotationSamplesMut = useMutation({
    mutationFn: async () => {
      if (!jwt || !workOrderDocumentId) throw new Error('Sesi tidak valid.');
      return strapiSyncQuotationSamplesForWorkOrder(jwt, workOrderDocumentId);
    },
    onSuccess: async () => {
      setSyncDialogError(null);
      setSyncDialogOpen(false);
      setSyncPreview(null);
      await qc.invalidateQueries({
        queryKey: ['strapi', 'quotationSampleSyncPreview', jwt, workOrderDocumentId],
      });
      await qc.invalidateQueries({ queryKey: queryKeys.surveyRowsPrefix(jwt) });
      if (workOrderDocumentId) {
        await qc.invalidateQueries({
          queryKey: queryKeys.surveyRowsForWorkOrder(jwt, workOrderDocumentId),
        });
      }
      await qc.invalidateQueries({
        queryKey: ['strapi', 'samplingPlansByWorkOrder', jwt, workOrderDocumentId],
      });
      if (workOrderDocumentId) {
        await qc.invalidateQueries({ queryKey: queryKeys.workOrderDetail(jwt, workOrderDocumentId) });
      }
    },
    onError: (error) => {
      setSyncDialogError(error instanceof Error ? error.message : 'Gagal menyinkronkan sample quotation.');
    },
  });

  const samples = detailQ.data?.sample_informations ?? [];
  const quotationUsesGroups = quotationUsesSampleGroups(detailQ.data?.quotation ?? {});
  const workOrderSamples = useMemo(() => {
    if (!quotationUsesGroups) return samples;
    return filterSamplesForWorkOrderSamplingPlan(
      samples,
      (plansQ.data ?? []).filter((plan) => !isAirGroupChecklistPlan(plan)),
      true,
    );
  }, [quotationUsesGroups, samples, plansQ.data]);
  const hasSamplingPlanGroups =
    samplesHaveAirGroup(workOrderSamples, 'air') || samplesHaveAirGroup(workOrderSamples, 'non_air');
  const samplingPlanChecklistReady = hasSamplingPlanGroups
    ? allVisibleSamplesHaveEquipmentChecklist(samples, plansQ.data ?? [], quotationUsesGroups)
    : true;
  const displayCustomer = resolveWorkOrderDisplayCustomer({
    sampling_schedules: detailQ.data?.sampling_schedules,
    sampling_schedule: detailQ.data?.sampling_schedule,
    quotation: detailQ.data?.quotation,
    samplingPlans: plansQ.data ?? [],
  });

  async function confirmPlanChecklistSaved() {
    await plansQ.refetch();
    await qc.invalidateQueries({ queryKey: ['strapi', 'samplingPlansByWorkOrder', jwt, workOrderDocumentId] });
    setPlanChecklistModalOpen(false);
  }

  const notStartedCount = displayRows.filter((row) => row.status === 'not_started').length;
  const samplingJobNumberReady = Boolean(String(detailQ.data?.job_number ?? '').trim());
  const quotationDocumentId = String(detailQ.data?.quotation?.documentId ?? '').trim();

  if (!workOrderDocumentId) {
    return (
      <PageShell title="Detail Sampling" fill>
        <Alert severity="warning">ID work order sampling tidak valid.</Alert>
      </PageShell>
    );
  }
  if (detailQ.isPending || plansQ.isPending || rowsQ.isPending) {
    return (
      <PageShell title="Detail Sampling" fill>
        <Box display="flex" justifyContent="center" py={4} sx={{ flex: 1 }}>
          <CircularProgress size={28} />
        </Box>
      </PageShell>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <PageShell title="Detail Sampling" fill>
        <Alert severity="error">
          {detailQ.error instanceof Error ? detailQ.error.message : 'Gagal memuat detail sampling.'}
        </Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      fill
      title="Detail Sampling"
      subtitle={detailQ.data.job_number ?? detailQ.data.documentId}
      extras={
        <>
          <Tabs value={tab} onChange={(_, value: SurveyDetailTab) => goTab(value)} sx={PAGE_SHELL_TABS_SX}>
            <Tab value="detail" label="Detail" data-testid="survey-tab-detail" />
            <Tab
              value="sampling_plan"
              label="Rencana pengambilan sampling"
              data-testid="survey-tab-sampling-plan"
            />
          </Tabs>
          {tab === 'sampling_plan' ? (
            <Typography variant="caption" color="text.secondary">
              Titik belum mulai: {notStartedCount} / {displayRows.length}
            </Typography>
          ) : null}
        </>
      }
      actions={
        <>
          {hasSamplingPlanGroups ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={
                samplingPlanChecklistReady ? (
                  <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: 'error.main' }} />
                )
              }
              onClick={() => setPlanChecklistModalOpen(true)}
              data-testid="survey-checklist-kesiapan-alat"
              aria-label={
                samplingPlanChecklistReady
                  ? 'Checklist kesiapan alat sudah disimpan'
                  : 'Checklist kesiapan alat belum disimpan'
              }
            >
              Checklist kesiapan alat
            </Button>
          ) : null}
          {quotationDocumentId ? (
            <Button
              size="small"
              variant="outlined"
              disabled={addQuotationSampleMut.isPending}
              onClick={() => {
                setEditQuotationError(null);
                setEditQuotationOpen(true);
              }}
              data-testid="survey-edit-quotation"
            >
              Edit Quotation
            </Button>
          ) : null}
          <Button size="small" component={RouterLink} to="/sampling/sampling">
            Daftar sampling
          </Button>
        </>
      }
    >
        {!samplingJobNumberReady ? (
          <Alert severity="warning" sx={{ mb: 1 }} data-testid="survey-awaiting-job-number">
            Admin sampling belum menyetujui tim survey. Nomor job belum terbit, petugas belum
            dapat memulai sampling.
          </Alert>
        ) : null}
        {editQuotationError ? (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setEditQuotationError(null)}>
            {editQuotationError}
          </Alert>
        ) : null}
        {editQuotationSuccess ? (
          <Alert severity="success" sx={{ mb: 1 }} onClose={() => setEditQuotationSuccess(null)}>
            {editQuotationSuccess}
          </Alert>
        ) : null}
        {detailQ.data.quotation?.progress_status === 'need_attention' ? (
          <Alert severity="warning" sx={{ mb: 1 }} data-testid="survey-quotation-need-attention">
            Quotation berstatus Need Attention. Penerimaan sampel diblokir sampai marketing
            selesai: PIC → SPV → manajer (bila ≥ Rp 20 juta) → pelanggan setuju.
          </Alert>
        ) : null}
        {tab === 'detail' ? (
          <Stack spacing={1} sx={{ overflow: 'auto' }}>
            <Typography variant="body2">Tanggal WO: {detailQ.data.work_order_date ?? '—'}</Typography>
            <Typography variant="body2">Status: {workOrderStatusDisplay(detailQ.data.progress_status)}</Typography>
            <SurveyCustomerAddressForm
              customer={displayCustomer}
              jwt={jwt}
              workOrderDocumentId={workOrderDocumentId}
            />
          </Stack>
        ) : (
          <CollapsibleKeteranganSplit
            toggleTestId="survey-keterangan-toggle"
            keterangan={
              <Stack spacing={1}>
                {planChangeSummary ? (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    <Typography variant="body2" sx={{ mb: 0.75 }}>
                      {workOrderPlanChangeBannerMessage(planChangeSummary)}
                    </Typography>
                    <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5, mb: 1 }}>
                      {planChangeSummary.plan_summaries.map((plan) => (
                        <Typography key={plan.sampling_plan_document_id} component="li" variant="body2">
                          {plan.bidang_pengujian_label} — {plan.pending_point_count} titik (
                          {plan.kind === 'new_plan' ? 'rencana baru' : 'titik tambahan'}:{' '}
                          {plan.point_labels.join(', ')})
                        </Typography>
                      ))}
                    </Stack>
                    <Button
                      component={RouterLink}
                      to={`/sampling/work-order/${encodeURIComponent(workOrderDocumentId)}/view#sampling_plan`}
                      size="small"
                      variant="outlined"
                    >
                      Lihat rencana di work order
                    </Button>
                  </Alert>
                ) : null}

                {!samplingPlanChecklistReady ? (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Lengkapi checklist kesiapan alat (rencana sampling air / non-air) sebelum memulai
                    titik.
                  </Alert>
                ) : null}
                {displayRows.length === 0 ? (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Belum ada baris berita acara untuk work order ini.
                  </Alert>
                ) : null}

                <SurveyWorkOrderPlansReadonly
                  plans={plansQ.data ?? []}
                  surveyRows={displayRows}
                  sampleLines={detailQ.data?.sample_informations ?? []}
                />
              </Stack>
            }
          >
            <SurveyBeritaAcaraGrid
              rows={displayRows}
              loading={rowsQ.isFetching || plansQ.isFetching}
              highlightRowKeys={planChangeSummary?.pending_row_keys}
              workOrderDocumentId={workOrderDocumentId}
              titikPlanOptions={titikPlanOptions}
              equipmentChecklistReady={samplingPlanChecklistReady}
              defaultRecipientEmail={
                quotationCustomerEmail(displayCustomer) ||
                quotationCustomerEmail(detailQ.data?.quotation?.customer)
              }
            />
          </CollapsibleKeteranganSplit>
        )}

      {workOrderDocumentId && planChecklistModalOpen ? (
        <SurveyStartSamplingPlanModal
          open={planChecklistModalOpen}
          workOrderDocumentId={workOrderDocumentId}
          samples={samples}
          quotationUsesGroups={quotationUsesGroups}
          onClose={() => setPlanChecklistModalOpen(false)}
          onConfirm={confirmPlanChecklistSaved}
        />
      ) : null}

      <SurveyQuotationSampleSyncDialog
        open={syncDialogOpen}
        preview={syncPreview}
        submitting={syncQuotationSamplesMut.isPending}
        error={syncDialogError}
        onConfirm={() => {
          setSyncDialogError(null);
          syncQuotationSamplesMut.mutate();
        }}
      />

      <QuotationSampleFormDialog
        open={editQuotationOpen}
        onClose={() => {
          if (addQuotationSampleMut.isPending) return;
          setEditQuotationOpen(false);
        }}
        initialRow={null}
        onSave={(row) => addQuotationSampleMut.mutate(row)}
      />
    </PageShell>
  );
}
